/**
 * DataSourcesPanel — Onglet "Données" du tableau de bord admin.
 *
 * Affiche par catégorie les compteurs + un bouton "Mettre à jour" :
 *  - E-phy Phyto (PPP) : Herbicide / Fongicide / Insecticide / Régulateur / Autre
 *  - E-phy MFSC : Biostimulant / Engrais / Amendement / Additif / Autre
 *  - Cadastre IGN RPG : Réunion / Mayotte
 *  - CIRAD : modules embarqués
 *
 * Chaque bouton appelle l'endpoint correspondant et rafraîchit le statut.
 */
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Sprout, FlaskConical, Layers, Database, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";
import CadastreRefsSection from "@/components/admin/CadastreRefsSection";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COL = { ink: "#1A1A1A", sub: "#7A7A7A", green: "#2D6A4F", purple: "#8B5CF6", orange: "#E07A5F", blue: "#3B82F6" };

function StatLine({ label, value, color = COL.ink }) {
  return (
    <div className="flex justify-between items-baseline text-xs">
      <span className="text-neutral-500">{label}</span>
      <span className="font-bold tabular-nums" style={{ color }}>{value ?? "—"}</span>
    </div>
  );
}

function FormatDate({ iso }) {
  if (!iso) return <span className="text-neutral-400 italic">jamais</span>;
  try {
    const d = new Date(iso);
    return <span>{d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>;
  } catch {
    return <span>{iso.slice(0, 16).replace("T", " ")}</span>;
  }
}

export default function DataSourcesPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null);
  const [updateCheck, setUpdateCheck] = useState(null);
  const [checkingUpdate, setCheckingUpdate] = useState(false);

  async function loadStatus() {
    try {
      const res = await authFetch(`${API_BASE}/admin/data-sources/status`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      toast.error("Impossible de charger l'état des sources.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function checkUpdates(showToast = false) {
    setCheckingUpdate(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/data-sources/check-updates`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUpdateCheck(data);
      if (showToast) {
        if (data?.ephy?.has_update) {
          toast.info("Nouvelle version E-phy ANSES disponible — un bouton MAJ peut être lancé.", { duration: 6000 });
        } else if (data?.ephy?.error) {
          toast.warning(`Vérification : ${data.ephy.error}`);
        } else {
          toast.success("E-phy à jour ✓");
        }
      }
    } catch (err) {
      if (showToast) toast.error("Impossible de vérifier les mises à jour.");
    } finally {
      setCheckingUpdate(false);
    }
  }

  useEffect(() => {
    loadStatus();
    // Auto-check une fois au mount
    checkUpdates(false);
  }, []);

  async function refreshEphy(category, label) {
    setRefreshing(category);
    try {
      const res = await authFetch(`${API_BASE}/admin/data-sources/refresh-ephy?category=${encodeURIComponent(category)}`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      if (data.status === "already_running") { toast.info(data.message); return; }
      toast.info(`${label} : ${data.message}`, { duration: 6000 });
      // Suivi : statut relu toutes les 10 s pendant 5 min au plus, puis bilan de la mise à jour
      const startedAt = Date.now();
      const poll = async () => {
        if (Date.now() - startedAt > 5 * 60 * 1000) return;
        try {
          const r = await authFetch(`${API_BASE}/admin/data-sources/status`);
          const s = await r.json();
          const last = s?.ephy?.last_refresh;
          if (last?.status === "running") { setTimeout(poll, 10000); return; }
          await loadStatus();
          if (last?.status === "done") {
            const c = last.result?.catalogues || {};
            const f = (k) => c[k] ? `+${c[k].ajoutes} / −${c[k].retires}` : "—";
            toast.success(`E-Phy à jour (fichier ANSES du ${new Date(last.result?.published_at).toLocaleDateString("fr-FR")}) · phyto ${f("ephy_france_phyto.json")} · matières fertilisantes ${f("ephy_france_mfsc.json")}`, { duration: 12000 });
          } else if (last?.status === "error") {
            toast.error(`Mise à jour E-Phy échouée : ${last.error || "erreur inconnue"}. Les catalogues en service sont conservés.`, { duration: 12000 });
          }
        } catch { setTimeout(poll, 10000); }
      };
      setTimeout(poll, 8000);
    } catch (err) {
      toast.error(`${label} : ${err.message}`);
    } finally {
      setRefreshing(null);
    }
  }

  async function refreshRpg(region) {
    setRefreshing(`rpg_${region}`);
    try {
      const res = await authFetch(`${API_BASE}/admin/data-sources/refresh-rpg?region=${region}`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.info(data.message || `Mise à jour RPG ${region} lancée en arrière-plan.`, { duration: 6000 });
      // Polling : check status toutes les 15s pendant max 3 min
      const startedAt = Date.now();
      const poll = async () => {
        if (Date.now() - startedAt > 3 * 60 * 1000) return; // timeout 3 min
        await loadStatus();
        // Lire le status fraîchement chargé : s'il est encore 'running', re-poll
        try {
          const r = await authFetch(`${API_BASE}/admin/data-sources/status`);
          const s = await r.json();
          const last = s?.rpg?.last_refresh;
          if (last?.status === "running") {
            setTimeout(poll, 15000);
          } else if (last?.status === "done") {
            toast.success(`RPG ${region === "reunion" ? "Réunion" : "Mayotte"} : +${last.added} ajoutées, ${last.updated} mises à jour (${last.duration_seconds}s)`);
          } else if (last?.status === "error") {
            toast.error(`RPG ${region} : ${last.error || "Erreur inconnue"}`);
          }
        } catch (err) {
          console.warn("[RPG poll]", err);
        }
      };
      setTimeout(poll, 15000);
    } catch (err) {
      toast.error(`RPG ${region} : ${err.message}`);
    } finally {
      setRefreshing(null);
    }
  }

  async function refreshCirad() {
    setRefreshing("cirad");
    try {
      const res = await authFetch(`${API_BASE}/admin/data-sources/refresh-cirad`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.info(data.message, { duration: 8000 });
      loadStatus();
    } catch (err) {
      toast.error(`CIRAD : ${err.message}`);
    } finally {
      setRefreshing(null);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="w-8 h-8 animate-spin" style={{ color: COL.green }} /></div>;
  if (!status) return <p className="text-sm text-center py-10 text-neutral-500">Impossible de charger</p>;

  const phytoCats = [
    { key: "herbicide", label: "Herbicides", color: "#DC2626", icon: Sprout },
    { key: "fongicide", label: "Fongicides", color: "#0EA5E9", icon: FlaskConical },
    { key: "insecticide", label: "Insecticides", color: "#F59E0B", icon: AlertCircle },
    { key: "regulateur", label: "Régulateurs", color: "#8B5CF6", icon: Layers },
    { key: "autre", label: "Autres PPP", color: "#6B7280", icon: Info },
  ];
  const mfscCats = [
    { key: "biostimulant", label: "Biostimulants", color: "#10B981", icon: Sprout },
    { key: "engrais", label: "Engrais", color: "#2D6A4F", icon: Sprout },
    { key: "amendement", label: "Amendements", color: "#A16207", icon: Layers },
    { key: "additif", label: "Additifs", color: "#6B7280", icon: Info },
    { key: "support_culture", label: "Supports de culture", color: "#0891B2", icon: Layers },
    { key: "autre", label: "Autres MFSC", color: "#475569", icon: Info },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="data-sources-panel">
      {/* Header global */}
      <div className="bg-white border border-black/5 rounded-xl p-4 space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-bold flex items-center gap-2" style={{ color: COL.ink }}>
              <Database className="w-4 h-4" style={{ color: COL.green }} />
              Sources de données externes
            </h2>
            <p className="text-xs text-neutral-500 mt-0.5">
              Mettez à jour manuellement les catalogues officiels. Chaque catégorie peut être rafraîchie individuellement.
            </p>
          </div>
          <Button
            data-testid="check-updates-btn"
            onClick={() => checkUpdates(true)}
            disabled={checkingUpdate}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs gap-1.5 flex-shrink-0"
          >
            {checkingUpdate ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Vérifier les MAJ
          </Button>
        </div>
        {updateCheck?.ephy?.has_update && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px]" data-testid="ephy-update-banner">
            <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                Nouvelle version E-phy disponible
              </p>
              <p className="text-amber-700">
                ANSES a publié une mise à jour le{" "}
                <FormatDate iso={updateCheck.ephy.remote_published_at} />.
                Lancez une MAJ ci-dessous pour synchroniser.
              </p>
            </div>
          </div>
        )}
        {updateCheck?.ephy && !updateCheck.ephy.has_update && !updateCheck.ephy.error && updateCheck.ephy.remote_published_at && (
          <p className="text-[10px] text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Catalogue E-phy à jour (publié le <FormatDate iso={updateCheck.ephy.remote_published_at} />)
          </p>
        )}
      </div>

      {/* SECTION E-PHY PHYTO */}
      <section className="bg-white border border-black/5 rounded-xl p-5 space-y-3" data-testid="ephy-phyto-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: COL.ink }}>
              <FlaskConical className="w-4 h-4" style={{ color: COL.green }} />
              E-phy — Produits phytopharmaceutiques (PPP)
            </h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">Source : ANSES / data.gouv.fr · Total {status.ephy.phyto_total} produits autorisés</p>
            <p className="text-[10px] text-neutral-400 mt-1">Dernière MAJ : <FormatDate iso={status.ephy.last_refresh?.finished_at || status.ephy.last_refresh?.started_at} />
              {status.ephy.last_refresh?.status === "running" && " · mise à jour en cours…"}
              {status.ephy.last_refresh?.status === "error" && <span className="text-red-600"> · échec : {status.ephy.last_refresh.error}</span>}
              {status.ephy.last_refresh?.result?.published_at && <> · fichier ANSES du <FormatDate iso={status.ephy.last_refresh.result.published_at} /></>}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {phytoCats.map(cat => (
            <div key={cat.key} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-black/5 bg-neutral-50/50" data-testid={`phyto-${cat.key}-card`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cat.color + "15" }}>
                  <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: COL.ink }}>{cat.label}</p>
                  <p className="text-[11px] text-neutral-500 tabular-nums">{status.ephy.phyto[cat.key] ?? 0} produits</p>
                </div>
              </div>
              <Button
                data-testid={`refresh-phyto-${cat.key}-btn`}
                onClick={() => refreshEphy(`phyto_${cat.key}`, cat.label)}
                disabled={!!refreshing}
                size="sm"
                variant="outline"
                className="h-8 px-2.5 text-[11px] gap-1.5 flex-shrink-0"
              >
                {refreshing === `phyto_${cat.key}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                MAJ
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION E-PHY MFSC */}
      <section className="bg-white border border-black/5 rounded-xl p-5 space-y-3" data-testid="ephy-mfsc-section">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: COL.ink }}>
            <Sprout className="w-4 h-4" style={{ color: COL.green }} />
            E-phy — Matières fertilisantes & supports de culture (MFSC)
          </h3>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            Engrais, biostimulants, amendements (inclut Osiryl, NPK, etc.) · Total {status.ephy.mfsc_total} produits
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {mfscCats.map(cat => (
            <div key={cat.key} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-black/5 bg-neutral-50/50" data-testid={`mfsc-${cat.key}-card`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cat.color + "15" }}>
                  <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: COL.ink }}>{cat.label}</p>
                  <p className="text-[11px] text-neutral-500 tabular-nums">{status.ephy.mfsc[cat.key] ?? 0} produits</p>
                </div>
              </div>
              <Button
                data-testid={`refresh-mfsc-${cat.key}-btn`}
                onClick={() => refreshEphy(`mfsc_${cat.key}`, cat.label)}
                disabled={!!refreshing}
                size="sm"
                variant="outline"
                className="h-8 px-2.5 text-[11px] gap-1.5 flex-shrink-0"
              >
                {refreshing === `mfsc_${cat.key}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                MAJ
              </Button>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-neutral-400">
          Note : un seul ZIP CSV ANSES contient toutes les catégories. Chaque bouton déclenche le téléchargement complet et régénère tous les catalogues — utilisez l&apos;un ou l&apos;autre indifféremment, le résultat est identique.
        </p>
      </section>

      {/* SECTION CADASTRE RPG */}
      <section className="bg-white border border-black/5 rounded-xl p-5 space-y-3" data-testid="rpg-section">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: COL.ink }}>
            <Layers className="w-4 h-4" style={{ color: COL.blue }} />
            Cadastre agricole — RPG IGN
          </h3>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            Source : <strong>API IGN Geoplateforme WFS</strong> (Registre Parcellaire Graphique officiel)
          </p>
          <p className="text-[10px] text-neutral-400 mt-1">
            Dernière MAJ : <FormatDate iso={status.rpg.last_refresh?.finished_at || status.rpg.last_refresh?.started_at} />
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { region: "reunion", label: "🌋 La Réunion", count: status.rpg.reunion_count },
            { region: "mayotte", label: "🏝️ Mayotte", count: status.rpg.mayotte_count },
          ].map(r => (
            <div key={r.region} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-black/5 bg-neutral-50/50" data-testid={`rpg-${r.region}-card`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: COL.blue + "15" }}>
                  <Layers className="w-4 h-4" style={{ color: COL.blue }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: COL.ink }}>{r.label}</p>
                  <p className="text-[11px] text-neutral-500 tabular-nums">{r.count.toLocaleString("fr-FR")} parcelles</p>
                </div>
              </div>
              <Button
                data-testid={`refresh-rpg-${r.region}-btn`}
                onClick={() => refreshRpg(r.region)}
                disabled={!!refreshing}
                size="sm"
                variant="outline"
                className="h-8 px-2.5 text-[11px] gap-1.5 flex-shrink-0"
              >
                {refreshing === `rpg_${r.region}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                MAJ
              </Button>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-neutral-500 bg-amber-50/40 border border-amber-100 rounded-lg p-2.5 leading-relaxed">
          <strong>⚠️ Important :</strong> la mise à jour conserve l&apos;historique (carnet PRO, verrouillages, droits accordés)
          en faisant un upsert par <code className="text-amber-700">rpg_id</code>. Compte 30s à 2 min selon la taille.
        </div>
      </section>

      {/* SECTION RÉFÉRENCES CADASTRALES */}
      <CadastreRefsSection />

      {/* SECTION CIRAD */}
      <section className="bg-white border border-black/5 rounded-xl p-5 space-y-3" data-testid="cirad-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: COL.ink }}>
              <Database className="w-4 h-4" style={{ color: COL.purple }} />
              Données CIRAD
            </h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Méthodologie AMENDA · Carte pédologique Raunet 1991
            </p>
            <p className="text-[10px] text-neutral-400 mt-1">
              Dernière vérification : <FormatDate iso={status.cirad.last_refresh?.checked_at} />
            </p>
          </div>
          <Button
            data-testid="refresh-cirad-btn"
            onClick={refreshCirad}
            disabled={!!refreshing}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs gap-1.5"
          >
            {refreshing === "cirad" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Vérifier les modules
          </Button>
        </div>
        <div className="text-[11px] bg-purple-50/40 border border-purple-100 rounded-lg p-3 space-y-1.5">
          <p className="font-semibold text-purple-900">Modules CIRAD actuellement intégrés :</p>
          <ul className="space-y-0.5 list-disc list-inside text-neutral-700">
            {(status.cirad.modules || []).map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
          <p className="text-[10px] text-neutral-500 pt-1.5 border-t border-purple-100">
            Ces données sont <strong>codées en dur</strong> dans le backend (pas d&apos;API CIRAD publique en temps réel).
            Pour intégrer de nouveaux datasets (variétés, ravageurs, FertiDOM, etc.), fournissez les fichiers CSV/JSON à l&apos;équipe.
          </p>
        </div>
      </section>
    </div>
  );
}
